import React from 'react';

import { Alert, Box, Button } from '@mui/material';

import { Field, Form, FormSpy } from '@hrlogics/react-components/form';

import { Nullable } from '@clearverify/types';
import { toIds } from '@clearverify/utils';

import {
  AccessRestrictionMode,
  GetPeoStaffAccessInfoDocument,
  GetPeoStaffAccessInfoQueryVariables,
  PeoStaffBlacklistedEmployeeKindEnum,
  PeoStaffDocument,
  PeoStaffListDocument,
  PeoStaffQueryVariables,
  PeoStaffRole,
  QueryMode,
  UserStatus,
  usePeoStaffMinimalListQuery,
  useUpdatePeoStaffDetailsMutation,
} from 'src/api';
import { FullscreenLoader } from 'src/components/common';
import { SelectField } from 'src/components/finalForm';
import { PEO_ROLE_OPTIONS } from 'src/constants/peo';
import { useNotification } from 'src/hooks/app';
import { useEmployeeBlacklistAutocomplete } from 'src/hooks/employee';
import { useEmployersAutocomplete } from 'src/hooks/employer';
import { usePeoById } from 'src/hooks/peo';
import { usePeoStaff, usePeoStaffAccesses } from 'src/hooks/peoStaff';
import { enumToSelectOptions } from 'src/utils/common';

import {
  AccessRestrictionModeFormField,
  AccessRestrictionModeFormPart,
  convertEmployeeBlacklist,
} from '../AccessRestrictionModeFormPart';

import { PeoStaffViewDetailsModalHead } from './components/PeoStaffViewDetailsModalHead';
import { DetailsFormFields } from './constants';
import { DetailsFormValues } from './types';
import { getDetailsFormResolvedValidationSchema } from './utils';

interface PeoStaffViewDetailsModalFormAdvancedProps {
  peoStaffId: Nullable<string>;
  handleCloseModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
  canEditStatusAndRole: boolean;
  canSetupAccessRestriction: boolean;
}

export const PeoStaffViewDetailsModalFormAdvanced: React.FC<
  PeoStaffViewDetailsModalFormAdvancedProps
> = ({
  peoStaffId,
  handleCloseModal,
  switchClosability,
  canEditStatusAndRole,
  canSetupAccessRestriction,
}) => {
  const { createApolloContext } = useNotification();

  const { peoStaff, peoStaffRoleName, loading: peoStaffLoading } = usePeoStaff(peoStaffId);
  const [updatePeoStaffDetails] = useUpdatePeoStaffDetailsMutation();
  const {
    accessRestrictionMode,
    peoStaffBlacklists,
    peoStaffWhitelists,
    loading: peoStaffBlacklistsLoading,
  } = usePeoStaffAccesses(peoStaffId);

  const peoId = peoStaff?.peo.id;
  const { isPartnership } = usePeoById(peoId);

  const { data: peoStaffListData, loading: peoStaffListLoading } = usePeoStaffMinimalListQuery({
    variables: {
      filter: {
        peoId: { equals: peoStaff?.peo.id },
      },
    },
    skip: !peoStaff || !canEditStatusAndRole,
  });

  const employersSelectProps = useEmployersAutocomplete({
    queryFilter: searchText => ({
      filter: { displayName: { contains: searchText, mode: QueryMode.Insensitive } },
    }),
    queryOptions: {
      variables: {
        filter: { peoId: { equals: peoId } },
      },
      skip: !peoId || !canSetupAccessRestriction,
    },
  });

  const employeesSelectProps = useEmployeeBlacklistAutocomplete({
    peoId,
    skip: !peoId || !canSetupAccessRestriction,
  });

  const initialValues = React.useMemo(
    (): Partial<DetailsFormValues> => ({
      [DetailsFormFields.userStatus]: peoStaff?.user.status,
      [DetailsFormFields.businessRole]: peoStaff?.role,
      [AccessRestrictionModeFormField.accessRestrictionMode]: accessRestrictionMode,
      [AccessRestrictionModeFormField.employerBlacklist]:
        peoStaffBlacklists?.blacklistedEmployers || [],
      [AccessRestrictionModeFormField.employeeBlacklist]: (
        peoStaffBlacklists?.blacklistedEmployees || []
      ).map(employee => ({
        kind: PeoStaffBlacklistedEmployeeKindEnum.Existing,
        id: employee.id,
        last4OfSsn: employee.last4OfSsn,
        fullName: employee.fullName,
      })),
      [AccessRestrictionModeFormField.employerWhitelist]:
        peoStaffWhitelists?.whitelistedEmployers || [],
    }),
    [
      peoStaff?.user.status,
      peoStaff?.role,
      accessRestrictionMode,
      peoStaffBlacklists?.blacklistedEmployers,
      peoStaffBlacklists?.blacklistedEmployees,
      peoStaffWhitelists?.whitelistedEmployers,
    ],
  );

  const onSubmit = React.useCallback(
    async (formValues: DetailsFormValues) => {
      switchClosability(false);

      const isBusinessRoleUpdated =
        DetailsFormFields.businessRole in formValues && formValues.businessRole !== peoStaff?.role;
      const isUserStatusUpdated =
        DetailsFormFields.userStatus in formValues &&
        formValues.userStatus !== peoStaff?.user.status;

      const isSuperPeoAdmin =
        DetailsFormFields.businessRole in formValues &&
        formValues.businessRole === PeoStaffRole.PeoSuperAdmin;

      // Only touch the access-restriction section when it is actually editable and the
      // target isn't a Super Admin. Populate only the active mode's list and clear the
      // other mode's lists, so switching modes never leaves stale entries in the DB. For
      // partnership PEOs the employee blacklist field is hidden but keeps its server-loaded
      // values in form state, so sending them back is a no-op rather than a wipe.
      const accessRestriction =
        !canSetupAccessRestriction || isSuperPeoAdmin
          ? undefined
          : formValues.accessRestrictionMode === AccessRestrictionMode.Whitelist
            ? {
                accessRestrictionMode: AccessRestrictionMode.Whitelist,
                employersWhitelistIdList: toIds(formValues.employerWhitelist),
                employersBlacklistIdList: [],
                employeesBlacklistList: [],
              }
            : {
                accessRestrictionMode: AccessRestrictionMode.Blacklist,
                employersBlacklistIdList: toIds(formValues.employerBlacklist),
                employeesBlacklistList: convertEmployeeBlacklist(formValues.employeeBlacklist),
                employersWhitelistIdList: [],
              };

      try {
        await updatePeoStaffDetails({
          variables: {
            peoStaffId: peoStaffId || '',
            data: {
              role: isBusinessRoleUpdated ? formValues.businessRole : undefined,
              userStatus: isUserStatusUpdated ? formValues.userStatus : undefined,
              accessRestriction,
            },
          },
          context: createApolloContext({ successMessage: 'Updated successfully' }),
          // The mutation returns a bare SuccessResponse, so refetch everything the modal
          // and the staff table read.
          refetchQueries: [
            PeoStaffListDocument,
            {
              query: PeoStaffDocument,
              variables: { id: peoStaffId || '' } satisfies PeoStaffQueryVariables,
            },
            {
              query: GetPeoStaffAccessInfoDocument,
              variables: { id: peoStaffId || '' } satisfies GetPeoStaffAccessInfoQueryVariables,
            },
          ],
          // Close the modal only after the refetches land, so the staff table and a
          // quickly-reopened modal never show stale data.
          awaitRefetchQueries: true,
        });

        switchClosability(true);
        handleCloseModal();
      } catch {
        switchClosability(true);
      }
    },
    [
      switchClosability,
      peoStaff?.role,
      peoStaff?.user.status,
      canSetupAccessRestriction,
      updatePeoStaffDetails,
      createApolloContext,
      peoStaffId,
      handleCloseModal,
    ],
  );

  const disableNonAdminRoleChange = React.useMemo(() => {
    const peoStaffList = peoStaffListData?.peoStaffList || [];
    const peoStaffListWithoutCurrent = peoStaffList.filter(staff => staff.id !== peoStaff?.id);

    if (peoStaffListWithoutCurrent.length === 0) return true;
    if (
      (peoStaff?.role === PeoStaffRole.PeoAdmin || peoStaff?.role === PeoStaffRole.PeoSuperAdmin) &&
      peoStaffListWithoutCurrent.every(
        staff => staff.user.status !== UserStatus.Active || staff.role === PeoStaffRole.PeoManager,
      )
    ) {
      return true;
    }

    return false;
  }, [peoStaff?.id, peoStaff?.role, peoStaffListData?.peoStaffList]);

  if (!peoStaff || peoStaffLoading || peoStaffBlacklistsLoading || peoStaffListLoading) {
    return <FullscreenLoader />;
  }

  return (
    <React.Fragment>
      <PeoStaffViewDetailsModalHead
        peoStaff={peoStaff}
        peoStaffRoleName={peoStaffRoleName}
        showRole={!canEditStatusAndRole}
        showStatus={!canEditStatusAndRole}
      />
      <Form<DetailsFormValues>
        onSubmit={onSubmit}
        initialValues={initialValues}
        subscription={{ valid: true, submitting: true, dirty: true }}
        containerProps={{ mt: 2 }}
        validationSchema={getDetailsFormResolvedValidationSchema(canEditStatusAndRole)}
      >
        {({ submitting, valid, dirty }) => {
          return (
            <React.Fragment>
              <Box display="flex" flexDirection="column" gap={2} mt={2}>
                {canEditStatusAndRole && (
                  <React.Fragment>
                    <Field
                      name={DetailsFormFields.userStatus}
                      label="Status"
                      component={SelectField}
                      fullWidth
                      disabled={submitting}
                      options={enumToSelectOptions(UserStatus)}
                    />

                    <Field
                      name={DetailsFormFields.businessRole}
                      label="Role"
                      component={SelectField}
                      fullWidth
                      disabled={submitting || disableNonAdminRoleChange}
                      options={PEO_ROLE_OPTIONS}
                    />

                    {disableNonAdminRoleChange && (
                      <Alert severity="warning">
                        The role cannot be changed. There are no other active Admin users under this
                        PEO.
                      </Alert>
                    )}
                  </React.Fragment>
                )}

                <FormSpy<DetailsFormValues> subscription={{ values: true }}>
                  {({ values }) => {
                    const isSuperPeoAdmin =
                      DetailsFormFields.businessRole in values &&
                      values.businessRole === PeoStaffRole.PeoSuperAdmin;
                    if (!canSetupAccessRestriction || isSuperPeoAdmin) return null;

                    const selectedAccessRestrictionMode = values.accessRestrictionMode;

                    return (
                      <AccessRestrictionModeFormPart
                        renderFieldName={fieldName => fieldName}
                        selectedAccessRestrictionMode={selectedAccessRestrictionMode}
                        employersSelectProps={employersSelectProps}
                        employeesSelectProps={employeesSelectProps}
                        employersOnly={isPartnership}
                      />
                    );
                  }}
                </FormSpy>
              </Box>

              <Box display="flex" gap={2} mt={4} justifyContent="center">
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleCloseModal}
                  disabled={submitting}
                >
                  Close
                </Button>

                {(canEditStatusAndRole || canSetupAccessRestriction) && (
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={!valid || submitting || !dirty}
                    loading={submitting}
                  >
                    Save Changes
                  </Button>
                )}
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </React.Fragment>
  );
};
